"use client";

import React, { useRef } from "react";
import { Play, CircleAlert, Video } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function VideoGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="181060" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-none shadow-sm bg-card rounded-3xl">
            <AspectRatio ratio={16 / 9} className="bg-muted/50 animate-pulse rounded-3xl" />
            <CardContent className="p-5">
              <div className="mb-3 h-6 w-3/4 animate-pulse rounded-full bg-muted/50" />
              <div className="h-4 w-1/2 animate-pulse rounded-full bg-muted/50" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-3xl border border-border/50 bg-card py-16 text-center shadow-sm">
        <CircleAlert className="mb-4 size-10 text-primary" />
        <p className="mb-6 text-foreground font-default">Hoppla! Die Video-Tour konnte nicht geladen werden.</p>
        <Button variant="outline" onClick={refetch} className="rounded-full font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground">Erneut versuchen</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-border/50 bg-card shadow-sm">
        <AspectRatio ratio={16 / 9} className="bg-black">
          <video className="size-full" controls autoPlay muted playsInline preload="metadata">
            <source src="https://perfect-duck.10web.cloud/wp-content/uploads/2026/04/Konferenzraum.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </AspectRatio>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-none bg-card shadow-sm transition-all duration-300 hover:shadow-md rounded-3xl">
          <Link to={post.link || "#"} className="block h-full">
            <AspectRatio ratio={16 / 9} className="relative overflow-hidden bg-secondary rounded-3xl">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="size-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="flex size-full items-center justify-center bg-secondary">
                  <Video className="size-12 text-muted-foreground" />
                </div>}
              
              <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors duration-300 group-hover:bg-black/30">
                <div className="flex size-14 items-center justify-center rounded-full bg-primary shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                  <Play className="ml-1 size-6 text-primary-foreground" fill="currentColor" />
                </div>
              </div>
            </AspectRatio>
            
            <CardContent className="p-5">
              <h3 className="line-clamp-2 text-lg font-default font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                {post.title}
              </h3>
              {post.date && <p className="mt-3 text-sm font-default text-muted-foreground">
                  {new Date(post.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
                </p>}
            </CardContent>
          </Link>
        </Card>)}
    </div>;
}
export default function VideoLive() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="videotour" className="px-3 sm:px-4 ![padding-top:144px] ![padding-bottom:144px]">
  <div className="max-w-7xl mx-auto">
    <div className="relative h-[80vh] w-full rounded-[1rem] overflow-hidden">
      <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover pointer-events-none" autoPlay muted loop playsInline preload="auto">
        <source src="https://kassel-coworking.de/wp-content/uploads/2026/07/Coworking-Reel-web.webm" type="video/webm" />
        <source src="https://kassel-coworking.de/wp-content/uploads/2026/07/Coworking-Reel-web.mp4" type="video/mp4" />
      </video>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10 pointer-events-none" />

      {/* Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-start justify-end p-10 md:p-16">
        <span className="mb-4 inline-block rounded-full bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-2 text-sm font-semibold uppercase tracking-widest text-white/80">
          Virtueller Rundgang
        </span>
        <h2 className="font-sans text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tighter leading-[1.05] max-w-2xl">
          Dein Raum für die<br />nächste große Idee.
        </h2>
      </div>
    </div>
  </div>
</section>;
}